package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.StoreAppDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StorePushDto
import dev.lingvo.shared.StorePushStatus
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation
import org.jetbrains.compose.web.dom.Text

/** One bound app's pushes and rollbacks, newest first, with the two ways to act on one. */
@Composable
internal fun StoreHistorySection(
    app: StoreAppDto,
    subject: ProjectAuthoritySubject?,
    slug: String,
    revision: Int,
    runner: StoreActionRunner,
    onListingChanged: () -> Unit,
) {
    val platform = app.platform
    var localReload by remember(subject, app.id) { mutableStateOf(0) }
    val state = loadReloadable(subject, app.id, reloadToken = revision + localReload) {
        val current = subject ?: awaitCancellation()
        requestProjectResource(key = current.action, expectedSlug = slug) { route ->
            ApiClient.storePushes(route.project.slug, platform)
        }
    }
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LSectionTitle(platform.displayName)
        when (val snapshot = state.state) {
            Async.Loading -> LoadingState(rows = 2)
            is Async.Failure -> AsyncFailureState(
                snapshot,
                subject = t("app.projects.slug.stores_history.the_push_history", "the push history"),
                onRetry = { localReload++ },
            )
            is Async.Success -> if (snapshot.value.isEmpty()) {
                LMuted(t("app.projects.slug.stores_history.no_pushes", "Nothing has been pushed to this store from Lingvo yet."))
            } else {
                StorePushTable(
                    pushes = snapshot.value,
                    platform = platform,
                    slug = slug,
                    runner = runner,
                    onChanged = onListingChanged,
                )
            }
        }
    }
}

@Composable
private fun StorePushTable(
    pushes: List<StorePushDto>,
    platform: StorePlatform,
    slug: String,
    runner: StoreActionRunner,
    onChanged: () -> Unit,
) {
    var rollingBack by remember(platform) { mutableStateOf<StorePushDto?>(null) }
    // A rollback the server would only half do: the push, and the server's own list of what stays live.
    var incomplete by remember(platform) { mutableStateOf<Pair<StorePushDto, String>?>(null) }
    var busy by remember(platform) { mutableStateOf(false) }
    var actingOn by remember(platform) { mutableStateOf<String?>(null) }

    fun act(push: StorePushDto, rollback: Boolean, acknowledge: Boolean = false) {
        actingOn = push.id
        runner.run(
            setBusy = { busy = it },
            failure = if (rollback) {
                tr("app.projects.slug.stores_history.rollback_failed", "Couldn't roll back this push. Retry?")
            } else {
                tr("app.projects.slug.stores_history.retry_failed", "Couldn't retry this push. Retry?")
            },
            transport = {
                if (rollback) ApiClient.rollbackStorePush(slug, platform, push.id, acknowledge) else ApiClient.retryStorePush(slug, platform, push.id)
            },
            onError = { error ->
                rollingBack = null
                if (rollback && !acknowledge && error.isStoreCode(STORE_ROLLBACK_INCOMPLETE)) {
                    incomplete = push to ((error as? ApiException)?.message.orEmpty())
                    true
                } else {
                    false
                }
            },
        ) { result ->
            rollingBack = null
            val ok = result.status == StorePushStatus.SUCCEEDED
            runner.notify(
                when {
                    ok && rollback -> tr("app.projects.slug.stores_history.rolled_back_toast", "Rolled back. The store has its previous text again.")
                    ok -> tr("app.projects.slug.stores_history.retried_toast", "Sent. The store accepted the queued text.")
                    else -> trf(
                        "app.projects.slug.stores_history.result_toast",
                        "The store answered: {status}. See the history for details.",
                        "status" to storePushStatusLabel(result.status),
                    )
                },
                if (ok) LToastVariant.SUCCESS else LToastVariant.WARNING,
                4_000,
            )
            onChanged()
        }
    }

    LTable(
        caption = tf("app.projects.slug.stores_history.caption", "{store} push history", "store" to platform.displayName),
        captionHidden = true,
    ) {
        LTableHead {
            LTableRow {
                LTableHeaderCell { Text(t("app.projects.slug.stores_history.when", "When")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_history.who", "Who")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_history.kind", "Kind")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_history.status", "Status")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_history.languages", "Languages")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_history.actions", "Actions")) }
            }
        }
        LTableBody {
            pushes.forEach { push ->
                LTableRow {
                    LTableCell { Text(formatUtc(push.createdAt, LTimeStyle.MINUTE)) }
                    LTableCell { Text(push.actorLabel) }
                    LTableCell { Text(storePushKindLabel(push.kind)) }
                    LTableCell {
                        LBadge(storePushStatusLabel(push.status), variant = storePushStatusVariant(push.status))
                    }
                    LTableCell { StorePushDetail(push) }
                    LTableCell {
                        Row(Modifier.gap(Space.XS)) {
                            if (push.canRollback) {
                                LButton(
                                    t("app.projects.slug.stores_history.roll_back", "Roll back"),
                                    onClick = { rollingBack = push },
                                    variant = LButtonVariant.SECONDARY,
                                    size = LButtonSize.SM,
                                    enabled = runner.canManage && !busy,
                                )
                            }
                            if (push.status == StorePushStatus.QUEUED) {
                                LButton(
                                    t("app.projects.slug.stores_history.retry", "Retry"),
                                    onClick = { act(push, rollback = false) },
                                    variant = LButtonVariant.SECONDARY,
                                    size = LButtonSize.SM,
                                    enabled = runner.canManage && !busy,
                                    loading = busy && actingOn == push.id,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
    if (!runner.canManage && pushes.any { it.canRollback || it.status == StorePushStatus.QUEUED }) {
        LMuted(t("app.projects.slug.stores_history.actions_need_admin", "Rolling back and retrying reach the live store, so an owner or admin does them."))
    }
    LConfirmDialog(
        open = rollingBack != null,
        title = t("app.projects.slug.stores_history.rollback_title", "Roll back this push?"),
        confirmLabel = t("app.projects.slug.stores_history.roll_back", "Roll back"),
        onConfirm = { rollingBack?.let { act(it, rollback = true) } },
        onCancel = { rollingBack = null },
        tone = LConfirmTone.DANGER,
        consequenceTitle = t("app.projects.slug.stores_history.rollback_consequence_title", "The store's previous text goes live again"),
        consequence = tf(
            "app.projects.slug.stores_history.rollback_consequence",
            "This restores the text {store} held before this push, for {locales}. The project's own text is not changed, " +
                "and the rollback is recorded here as a push of its own.",
            "store" to platform.displayName,
            "locales" to rollingBack?.pushedLocales?.joinToString(", ").orEmpty(),
        ),
        busy = busy,
    )
    LConfirmDialog(
        open = incomplete != null,
        title = t("app.projects.slug.stores_history.incomplete_title", "Some fields can't be restored"),
        confirmLabel = t("app.projects.slug.stores_history.roll_back_the_rest", "Roll back the rest"),
        onConfirm = {
            incomplete?.first?.let { pushed ->
                incomplete = null
                act(pushed, rollback = true, acknowledge = true)
            }
        },
        onCancel = { incomplete = null },
        tone = LConfirmTone.DANGER,
        consequenceTitle = t("app.projects.slug.stores_history.incomplete_consequence_title", "Part of this push stays live"),
        consequence = incomplete?.second.orEmpty(),
        busy = busy,
    )
}

/** What a push reached, what it did not and why, and what it is waiting for. */
@Composable
private fun StorePushDetail(push: StorePushDto) {
    Column(Modifier.gap(Space.XXS)) {
        Text(push.pushedLocales.joinToString(", ").ifEmpty { "—" })
        if (push.failedLocales.isNotEmpty()) {
            // Formatted first and passed in whole: one message per locale, then one for the list.
            val failed = push.failedLocales.entries.joinToString(", ") { (locale, code) -> failedLocaleText(locale, code) }
            LMuted(tf("app.projects.slug.stores_history.failed_locales", "Failed: {locales}", "locales" to failed))
        }
        push.queuedReason?.takeIf(String::isNotBlank)?.let { reason ->
            LMuted(tf("app.projects.slug.stores_history.queued_reason", "Waiting: {reason}", "reason" to reason))
        }
        push.errorCode?.let { code ->
            LMuted(tf("app.projects.slug.stores_history.error_code", "Store error: {code}", "code" to code))
        }
    }
}

private fun failedLocaleText(locale: String, code: String): String =
    trf("app.projects.slug.stores_history.failed_locale", "{locale} ({code})", "locale" to locale, "code" to code)
